import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { Calendar, Clock, AlertCircle, ArrowRight } from "lucide-react";

export default function DispatcherRequests() {
  const [requests, setRequests] = useState([]);
  const [facilityMap, setFacilityMap] = useState({});
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/requests").then((r) => setRequests(r.data));
    api.get("/facilities").then((r) => {
      const m = {};
      r.data.forEach((f) => (m[f.id] = f));
      setFacilityMap(m);
    });
  }, []);

  const filtered = requests.filter((r) => filter === "all" || (filter === "urgent" ? ["urgent", "emergency"].includes(r.urgency) : r.status === filter));

  return (
    <div className="space-y-6" data-testid="dispatcher-requests">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>All Requests</h1>
        <div className="flex gap-2 flex-wrap">
          {["all", "submitted", "broadcasting", "fully_filled", "completed", "urgent"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${filter === s ? "bg-brand-navy text-white" : "bg-white text-slate-600 border border-slate-200"}`}
            >
              {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3">
        {filtered.map((r) => {
          const f = facilityMap[r.facility_id];
          return (
            <Card key={r.id} className="hover:border-brand-teal transition-colors" data-testid={`disp-req-${r.id}`}>
              <CardContent className="p-5 grid grid-cols-12 gap-4 items-center">
                <div className="col-span-12 md:col-span-3">
                  <div className="font-bold text-slate-900">{r.position} × {r.count}</div>
                  <div className="text-xs text-slate-500">{f?.name}</div>
                </div>
                <div className="col-span-6 md:col-span-3 text-sm text-slate-600 space-y-1">
                  <div className="flex items-center gap-2"><Calendar className="w-3 h-3" />{r.shift_date}</div>
                  <div className="flex items-center gap-2"><Clock className="w-3 h-3" />{r.start_time}-{r.end_time}</div>
                </div>
                <div className="col-span-6 md:col-span-2">
                  {r.urgency !== "standard" && (
                    <span className={`status-pill ${r.urgency === "emergency" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                      <AlertCircle className="w-3 h-3 mr-1" /> {r.urgency}
                    </span>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2"><StatusPill status={r.status} /></div>
                <div className="col-span-6 md:col-span-2 flex md:justify-end">
                  <Link to={`/dispatch/requests/${r.id}`} className="text-brand-teal text-sm font-semibold hover:underline flex items-center gap-1" data-testid={`disp-req-open-${r.id}`}>
                    Open <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && <div className="text-center py-16 text-slate-500">No requests match this filter.</div>}
      </div>
    </div>
  );
}
